import React from 'react';
import { ArrowRight, Code2, Play, Shield, Zap, Lock } from 'lucide-react';
import TopBar from './common/TopBar';
import Footer from './common/Footer';
import Playground from './common/Playground';

// --------------------------- Main Component ----------------------------------
const HomeContent: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-white text-slate-800">
      <TopBar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-60 bg-[radial-gradient(60rem_60rem_at_20%_-10%,rgba(251,146,60,0.15),transparent_60%),radial-gradient(50rem_50rem_at_90%_10%,rgba(245,158,11,0.12),transparent_60%)]" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
                Flatten any JSON into&nbsp;
                <span className="bg-gradient-to-r from-orange-600 to-amber-500 bg-clip-text text-transparent">1‑level key/value</span>
                &nbsp;pairs.
              </h1>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                Paste your JSON and get a single, predictable map of keys for analytics, CSV export, and DB ingestion. Client-side demo is
                free. API access is subscription only.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="/playground" className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow">
                  <Play className="h-4 w-4" /> Try it in your browser
                </a>
                <a
                  href="#pricing"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-slate-800 hover:bg-white">
                  <ArrowRight className="h-4 w-4" /> View API plans
                </a>
              </div>
              <div className="mt-6 flex items-center gap-6 text-sm text-slate-600">
                <div className="inline-flex items-center gap-2">
                  <Shield className="h-4 w-4" /> No upload in demo — runs locally
                </div>
                <div className="inline-flex items-center gap-2">
                  <Zap className="h-4 w-4" /> Handles deeply nested arrays/objects
                </div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-orange-200 bg-white/70 p-4 shadow-sm">
                <div className="text-xs font-semibold text-slate-500 mb-2">Example Output</div>
                <pre className="h-64 overflow-auto rounded-xl bg-slate-900 p-4 text-amber-100 text-xs">
                  {/* eslint-disable-next-line prettier/prettier */}
{`{
  "user.name.first": "Sinta",
  "user.address.geo.lat": -7.7956,
  "user.emails[0]": "sinta@example.com",
  "user.emails[1]": "wijaya@example.org",
  "orders[0].items[0].sku": "A1",
  "orders[0].items[0].qty": 1,
  "orders[0].items[1].sku": "B2",
  "orders[0].items[1].qty": 2,
}`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 scroll-mt-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Deterministic keys',
                desc: 'Dot notation with optional bracketed array indices gives stable, machine-friendly keys.',
                icon: <Code2 className="h-5 w-5" />,
              },
              {
                title: 'Big JSON ready',
                desc: 'Streaming-friendly algorithm with cycle protection and depth guard.',
                icon: <Zap className="h-5 w-5" />,
              },
              {
                title: 'Privacy-first',
                desc: 'Playground flattens entirely in your browser — nothing leaves the page.',
                icon: <Shield className="h-5 w-5" />,
              },
            ].map(f => (
              <div key={f.title} className="rounded-2xl border border-amber-200 bg-white p-6 shadow-sm">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 text-white">
                  {f.icon}
                </div>
                <h3 className="font-semibold text-lg">{f.title}</h3>
                <p className="mt-1 text-slate-600 text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-4 scroll-mt-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <h2 className="text-2xl font-bold tracking-tight">How FlatJSON Works</h2>
              <ol className="mt-4 space-y-3 text-slate-700">
                <li>
                  <span className="font-semibold">1.</span> We recursively visit each node and build keys with your chosen delimiter.
                </li>
                <li>
                  <span className="font-semibold">2.</span> Arrays are indexed (<span className="font-mono">items[0]</span>) or dotted (
                  <span className="font-mono">items.0</span>).
                </li>
                <li>
                  <span className="font-semibold">3.</span> Non-object values are emitted at leaf paths. Objects/arrays keep expanding.
                </li>
                <li>
                  <span className="font-semibold">4.</span> Circular references and excessive depth are safely guarded.
                </li>
              </ol>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                <div className="text-xs font-semibold text-slate-500 mb-2">TypeScript snippet</div>
                <pre className="h-64 overflow-auto rounded-xl bg-slate-900 p-4 text-amber-100 text-xs">
                  {/* eslint-disable-next-line prettier/prettier */}
{`type Flat = Record<string, unknown>;
function flattenJSON(input: unknown, delimiter = '.'): Flat {
  return '';
}`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Playground */}
      <Playground {...{ height: '24rem' }} />

      {/* Pricing / CTA */}
      <section id="pricing" className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-1 gap-6 items-stretch">
            {/* <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-xl font-semibold">Free Playground</h3>
              <p className="mt-2 text-slate-600">In-browser flattener for testing and learning. No account needed.</p>
              <ul className="mt-4 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>Client-side only</li>
                <li>Sample & custom JSON</li>
                <li>Dot or bracketed arrays</li>
              </ul>
              <a href="/playground" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white">
                <Play className="h-4 w-4" /> Launch playground
              </a>
            </div> */}
            <div className="rounded-2xl border border-amber-300 bg-gradient-to-br from-orange-50 to-amber-50 p-6 shadow-sm">
              <h3 className="text-xl font-semibold">
                API Access{' '}
                <span className="ml-2 inline-flex items-center rounded-md bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                  Subscription
                </span>
              </h3>
              <p className="mt-2 text-slate-700">Integrate at scale with rate limits, auth tokens, and SLAs.</p>
              <ul className="mt-4 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>
                  POST <span className="font-mono font-bold">https://flati.fyi/flatten</span> with raw JSON
                </li>
                <li>
                  POST <span className="font-mono font-bold">https://flati.fyi/&#123;API_KEY&#125;/&#123;FORWARD_URL&#125;</span> with raw JSON
                </li>
                <li>Flatten github JSON events data before webhook processing</li>
                <li>Enjoy flattened HEADER data too!!!</li>
                <li>99.9% uptime • Priority support</li>
              </ul>
              <div className="mt-4 rounded-xl border border-amber-200 bg-white p-4">
                <div className="text-xs font-semibold text-slate-500 mb-2">Example CURL request</div>
                <pre className="overflow-auto rounded-lg bg-slate-900 p-3 text-amber-100 text-xs">
                  {/* eslint-disable-next-line prettier/prettier */}
{`curl -X POST https://flati.fyi/flatten \\
  -H "ApiKey: <API_KEY>" \\
  -H "Owner: user@email.com" \\
  -H "Content-Type: application/json" \\
  -d '{"user":{"name':'Sinta","emails":["a@x","b@y"]}}'`}
                </pre>
              </div>
              <div className="mt-4 rounded-xl border border-amber-200 bg-white p-4">
                <div className="text-xs font-semibold text-slate-500 mb-2">Example github/bitbucket webhook preprocessing</div>
                <pre className="overflow-auto rounded-lg bg-slate-900 p-3 text-amber-100 text-xs">
                  {/* eslint-disable-next-line prettier/prettier */}
{`https://flati.fyi/269fe201-0a65-411b-90e3-dd3f35a9a648/n8n.custom.domain.org/webhook/8f8137ae-a17d-432d-879a-28f748b12fac`}
                </pre>
              </div>
              <a href="#" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 px-4 py-2 text-white shadow">
                <Lock className="h-4 w-4" /> Get API key
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomeContent;
